/**
 * Home (`/home`, tab 0, ⌥0): the one cross-project surface (navigation-ia.md §3.3–3.4, artboard
 * `optimal/home-1440`). It merges Now, Inbox and Overview into three views over one dataset:
 *
 *   Needs you (default)   Blocked first, then Needs you (the Inbox's rows; `lib/needs-you.ts` is
 *                         the one count); with nothing waiting, the all-clear plus Running
 *   ?v=needs              the same view (kept for links)
 *   ?v=running            Running    (Now's runs, unified run state)
 *   ?v=projects           Projects   (one tile per project, plus ideas not yet sorted)
 *   ?project=<id>         filter (a tab's ◆/◑ badge links here)
 *   ?i=<kind>:<id>        the inspector in the shell's right-hand slot
 *
 * Keys: j/k move, Enter opens, x selects, Esc closes (useTriageKeys); `o` opens the inspected
 * item in its project tab; `g i` / `g n` / `g o` switch views. A run opens in its project tab.
 */

import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Button } from '@viko/transcript-ui'
import { Plus, X } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { AddProjectDialog } from '../components/AddProjectDialog.js'
import { useAppUi } from '../components/app-ui.js'
import { TalkCard } from '../components/call/CallButton.js' // viko-call
import { InboxInspector } from '../components/inbox/InboxInspector.js'
import { InboxTriage, type OpenIntent } from '../components/inbox/InboxTriage.js'
import { useInboxRows } from '../components/inbox/useInboxRows.js'
import { useNeedsYou } from '../components/inbox/useNeedsYou.js'
import { LockedPanel, UnlockOffer, useLocked } from '../components/lock/ContentLock.js'
import { NewProjectDialog } from '../components/NewProjectDialog.js'
import { NowBoard } from '../components/now/NowBoard.js'
import { ProjectList } from '../components/overview/ProjectList.js'
import { InspectorSlot, StatusActions } from '../components/shell/slots.js'
import { useProjectTabs } from '../components/tabs/useProjectTabs.js'
import { useWide } from '../components/useWide.js'
import { useBackend } from '../lib/backend.js'
import { useReturnFocus } from '../lib/focus-return.js'
import { type HomeView, homeHref, projectHref, runHref } from '../lib/ia.js'
import { isLockConsequence } from '../lib/locked-view.js'
import { isLive, qk, useProjects, useRuns } from '../lib/queries.js'
import { isTyping } from '../lib/shortcut.js'
import { InboxFooter } from './inbox.js'
import { Ideas } from './projects.js'
import './home.css'

export interface HomeSearch {
  v?: HomeView
  project?: string
  i?: string
  intent?: OpenIntent
}

export function validateHomeSearch(s: Record<string, unknown>): HomeSearch {
  return {
    ...(s.v === 'needs' || s.v === 'running' || s.v === 'projects' ? { v: s.v } : {}),
    ...(typeof s.project === 'string' && s.project.length <= 200 ? { project: s.project } : {}),
    ...(typeof s.i === 'string' && s.i.length <= 300 ? { i: s.i } : {}),
    ...(s.intent === 'reply' || s.intent === 'delegate' ? { intent: s.intent } : {}),
  }
}

export function HomePage() {
  const search = useSearch({ strict: false }) as HomeSearch
  const nav = useNavigate()
  const ui = useAppUi()
  const wide = useWide()
  const needs = useNeedsYou()
  const runs = useRuns()
  const projects = useProjects()
  const built = useInboxRows()
  // app-clarity: no "Everything" view; Home opens on what needs you.
  const view: HomeView = search.v ?? 'needs'
  const project = search.project ? projects.data?.find((p) => p.id === search.project) : undefined
  const running = (runs.data ?? []).filter(
    (r) => isLive(r) && (!search.project || r.projectId === search.project),
  ).length
  const needCount = search.project ? needs.of(search.project) : needs.total
  const blockedCount = search.project ? needs.blockedOf(search.project) : needs.blocked
  // Locked (lib/content-lock.ts): encrypted counts are unknown here — "—", never 0.
  const locked = useLocked()
  const alsoNeeds = built.rows.filter((r) => !isLockConsequence(r)).length
  // Locked: no count at all (a dangling "—" reads as broken); the lock panel says why.
  const count = (n: number) => (locked ? undefined : String(n))
  const projectCount = projects.data?.length ?? 0

  const go = (patch: Partial<HomeSearch>, replace = false) =>
    void nav({
      to: '/home' as never,
      search: ((s: HomeSearch) => {
        const next: HomeSearch = { ...s, ...patch }
        for (const k of Object.keys(next) as (keyof HomeSearch)[])
          if (next[k] === undefined) delete next[k]
        return next
      }) as never,
      replace,
    })
  const open = (i: string, intent?: OpenIntent) => go({ i, intent })
  const close = () => go({ i: undefined, intent: undefined })
  useReturnFocus(search.i)
  // After an answer, the next row opens (fast triage), else the inspector closes.
  const order = useRef<readonly string[]>([])
  const next = () => {
    const keys = order.current.filter((k) => !k.startsWith('batch:'))
    const at = search.i ? keys.indexOf(search.i) : -1
    const k = at >= 0 ? (keys[at + 1] ?? keys[at - 1]) : undefined
    setTimeout(() => {
      if (location.pathname !== '/home') return
      if (k) open(k)
      else close()
    }, 0)
  }
  // app-clarity: Home opens on the top decision (desktop: in the inspector beside the list; phone:
  // the decision itself, answer zone at thumb height). Once per visit: closing it, or going back to
  // the list, keeps the list.
  const autoOpened = useRef(false)
  useEffect(() => {
    // An explicit view (`?v=needs`, `g i`) is the list itself; plain Home opens the decision.
    // A Home that opened locked keeps its list after the unlock (the offer and counts first).
    if (locked) autoOpened.current = true
    if (autoOpened.current || search.i || search.v || !needs.ready) return
    const top = order.current.find(
      (k) => !k.startsWith('batch:') && built.rows.find((r) => r.key === k)?.kind === 'decision',
    )
    if (!top) return
    autoOpened.current = true
    go({ i: top }, true)
  })
  // "Needs you 1 of 11 · Next: Toolbar look": where the open item sits in the list.
  const listKeys = order.current.filter(
    (k) => !k.startsWith('batch:') && !!built.rows.find((r) => r.key === k)?.blocking,
  )
  const at = search.i ? listKeys.indexOf(search.i) : -1
  const position =
    at >= 0
      ? {
          at: at + 1,
          total: listKeys.length,
          next: built.rows.find((r) => r.key === listKeys[at + 1])?.title,
          label: 'Needs you',
        }
      : undefined
  const step = (dir: 1 | -1) => {
    const k = listKeys[at + dir]
    if (k) go({ i: k }, true)
  }
  const runOf = (runId: string) => runs.data?.find((r) => r.runId === runId)
  const openRun = (runId: string) => void nav({ href: runHref(runId, runOf(runId)?.projectId) })

  // `o`: open the inspected item inside its project tab (same inspector, project chrome).
  const inspectedProject = search.i
    ? built.rows.find((r) => r.key === search.i)?.projectId
    : undefined
  useEffect(() => {
    if (!search.i || !inspectedProject) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'o' || e.metaKey || e.ctrlKey || e.altKey || isTyping(e)) return
      e.preventDefault()
      void nav({ href: projectHref(inspectedProject, '', search.i) })
    }
    addEventListener('keydown', onKey)
    return () => removeEventListener('keydown', onKey)
  }, [search.i, inspectedProject, nav])

  const needsList = (footer: boolean) => (
    <InboxTriage
      key={`needs:${search.project ?? ''}`}
      embedded
      {...(search.i ? { current: search.i } : {})}
      {...(search.project ? { projectId: search.project } : {})}
      onOpen={open}
      onClose={close}
      orderRef={order}
      preselect={wide}
      {...(footer ? { footer: <InboxFooter /> } : {})}
    />
  )
  const runningList = (keys: boolean) => (
    <NowBoard
      key={`running:${search.project ?? ''}`}
      embedded
      keysEnabled={keys}
      {...(search.project ? { projectId: search.project } : {})}
      {...(search.i ? { currentDecision: search.i } : {})}
      onOpen={openRun}
      onClose={close}
      onOpenInbox={() => go({ v: 'needs' })}
      onOpenDecision={open}
    />
  )

  const tabs: { id: HomeView; label: string; n?: string; keys: string }[] = [
    { id: 'needs', label: 'Needs you', n: count(needCount), keys: 'g i' },
    { id: 'running', label: 'Running', n: count(running), keys: 'g n' },
    { id: 'projects', label: 'Projects', n: count(projectCount), keys: 'g o' },
  ]

  return (
    <div className="hm-page">
      <div className="hm-scroll">
        <header className="hm-head">
          <h1>Home</h1>
          <nav className="hm-views" aria-label="Home views">
            {tabs.map((t) => (
              <Link
                key={t.label}
                to={homeHref({
                  ...(t.id !== 'needs' ? { v: t.id } : {}),
                  ...(search.project ? { project: search.project } : {}),
                })}
                className="hm-view"
                aria-current={view === t.id ? 'page' : undefined}
              >
                <span>{t.label}</span>
                {t.n !== undefined ? <b>{t.n}</b> : null}
                <kbd>{t.keys}</kbd>
              </Link>
            ))}
          </nav>
          {locked ? null : (
            <p className="hm-sum" data-testid="home-summary">
              {blockedCount ? (
                <>
                  <Link
                    className="hm-sum__blocked"
                    to={homeHref({ ...(search.project ? { project: search.project } : {}) })}
                  >
                    {blockedCount} blocked
                  </Link>
                  {', '}
                </>
              ) : null}
              <Link to={homeHref({ ...(search.project ? { project: search.project } : {}) })}>
                {needCount - blockedCount} {needCount - blockedCount === 1 ? 'needs' : 'need'} you
              </Link>
              {', '}
              <Link
                to={homeHref({
                  v: 'running',
                  ...(search.project ? { project: search.project } : {}),
                })}
              >
                {running} running
              </Link>
              {', across '}
              <Link to={homeHref({ v: 'projects' })}>
                {projectCount} {projectCount === 1 ? 'project' : 'projects'}
              </Link>
              .
            </p>
          )}
        </header>
        {search.project ? (
          <p className="hm-filter">
            <span>
              Showing <strong>{project?.name ?? 'one project'}</strong> only.
            </span>
            <Link to={projectHref(search.project)} className="hm-link">
              Open its tab
            </Link>
            <button
              type="button"
              className="vk-iconbtn"
              aria-label="Show every project"
              onClick={() => go({ project: undefined })}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </p>
        ) : null}
        {/* viko-call: the call leads Home whenever something needs you (same count as below). While
            a decision is open beside the list, the top bar's Call stays one click away instead. */}
        {!locked ? <UnlockOffer /> : null}
        {!locked && !search.project && !search.i && view === 'needs' ? <TalkCard /> : null}
        {locked ? (
          <>
            <LockedPanel where="home" />
            {alsoNeeds ? (
              <Section title="Also needs you" n={String(alsoNeeds)}>
                {needsList(false)}
              </Section>
            ) : null}
          </>
        ) : null}
        <button
          type="button"
          className="hm-composer"
          aria-keyshortcuts="c"
          onClick={() => ui.openCapture()}
        >
          <span className="hm-composer__text">
            {project
              ? `Tell Viko anything about ${project.name}`
              : 'Tell Viko anything. It picks the project.'}
          </span>
          <span className="hm-composer__mode">New request</span>
          <span className="hm-composer__route">
            {project ? `to ${project.name}` : 'Viko routes it'}
          </span>
          <kbd>c</kbd>
        </button>

        {locked ? null : view === 'needs' ? (
          <>
            <section className="hm-sec" aria-label={`Needs you, ${needCount}`}>
              {needsList(true)}
            </section>
            {/* All clear: what is running instead (the clear state), one key from steering it. */}
            {needs.ready && !needCount ? (
              <Section title="Running" n={running} more={homeHref({ v: 'running' })}>
                {runningList(false)}
              </Section>
            ) : null}
          </>
        ) : view === 'running' ? (
          <section className="hm-sec" aria-label={`Running, ${running}`}>
            {runningList(true)}
          </section>
        ) : view === 'projects' ? (
          <section className="hm-sec" aria-label="Projects">
            <ProjectList />
            <ProjectActions />
            <Ideas />
          </section>
        ) : null}
      </div>
      {search.i ? (
        <InspectorSlot>
          <div className="vs-panel" data-home-inspector>
            <InboxInspector
              key={search.i}
              inspectKey={search.i}
              {...(search.intent ? { intent: search.intent } : {})}
              onClose={close}
              onDone={next}
              backLabel="Back to Home"
              {...(position ? { position, onStep: step } : {})}
              {...(inspectedProject
                ? {
                    extra: (
                      <Link
                        to={projectHref(inspectedProject, '', search.i)}
                        className="tr-detail__open"
                        aria-keyshortcuts="o"
                      >
                        Open in{' '}
                        {projects.data?.find((p) => p.id === inspectedProject)?.name ??
                          'its project'}
                      </Link>
                    ),
                  }
                : {})}
            />
          </div>
        </InspectorSlot>
      ) : null}
      <StatusActions>
        <span>
          <kbd>j</kbd> <kbd>k</kbd> move
        </span>
        <span>
          <kbd>⏎</kbd> open
        </span>
        {search.i && inspectedProject ? (
          <span>
            <kbd>o</kbd> open in project
          </span>
        ) : null}
      </StatusActions>
    </div>
  )
}

/** Home › Projects: start a project, or add one a machine already has. */
function ProjectActions() {
  const b = useBackend()
  const qc = useQueryClient()
  const nav = useNavigate()
  const tabs = useProjectTabs()
  const [dialog, setDialog] = useState<'new' | 'add'>()
  const open = (id: string) => {
    tabs.pin(id)
    void nav({ href: projectHref(id) })
  }
  return (
    <div className="hm-actions">
      <Button
        size="sm"
        variant="primary"
        icon={<Plus size={14} />}
        onClick={() => setDialog('new')}
      >
        New project
      </Button>
      <Button size="sm" onClick={() => setDialog('add')}>
        Add from a machine
      </Button>
      {dialog === 'new' ? (
        <NewProjectDialog
          key={b.artifactAccess.accountId}
          onClose={() => setDialog(undefined)}
          onCreated={open}
        />
      ) : null}
      {dialog === 'add' ? (
        <AddProjectDialog
          key={b.artifactAccess.accountId}
          onClose={() => setDialog(undefined)}
          onSaved={async (project) => {
            await qc.invalidateQueries({ queryKey: qk.projects })
            open(project.id)
          }}
        />
      ) : null}
    </div>
  )
}

function Section({
  title,
  n,
  more,
  children,
}: {
  title: string
  n: number | string
  more?: string
  children: ReactNode
}) {
  const id = useMemo(() => `hm-${title.toLowerCase().replace(/\W+/g, '-')}`, [title])
  return (
    <section className="hm-sec" aria-labelledby={id}>
      <h2 id={id} className="hm-sec__h">
        {more ? <Link to={more}>{title}</Link> : title} <span className="hm-sec__n">{n}</span>
      </h2>
      {children}
    </section>
  )
}
